import React from 'react'
import styled from 'styled-components'
import { Tooltip, Flex } from '@sketch/components'
import { capitalizeFirst } from '@sketch/utils'
import { ReactComponent as ExclamationMark } from '@sketch/icons/exclamation-mark-triangle-17'
import { IS_SHOW_DIRTY_ATTRIBUTES_ENABLED } from '../../constants'

/**
 * The original value of a style property that has drifted from its shared
 * (Text or Layer) style, and the label to describe it.
 *
 * `TValue` is a string for most properties, but a `Color` for the colour
 * attributes, which render a swatch rather than text.
 */
export type DirtyAttributes<TValue = string> = {
  originalValue: TValue
  originalProperty: string
}

/**
 * Gates dirty attributes behind `IS_SHOW_DIRTY_ATTRIBUTES_ENABLED`.
 *
 * Every component that renders a dirty indicator needs this, and each one used
 * to inline the same ternary over its own `dirtyAttributes` prop. Generic over
 * the whole object rather than over `DirtyAttributes`, because the gradient
 * attribute carries a differently shaped one.
 */
export function resolveDirtyAttributes<T>(
  dirtyAttributes: T | undefined
): T | undefined {
  return IS_SHOW_DIRTY_ATTRIBUTES_ENABLED ? dirtyAttributes : undefined
}

/**
 * This function is essentially formatting the original values
 * into a neat object, ready for comsumption by the tooltip above
 */
export const getDirtyAttributes = ({
  originalValueKey,
  valueToDisplay,
  originalValues,
  labelToDisplay,
}: {
  originalValueKey: string
  valueToDisplay?: string
  originalValues?: any
  labelToDisplay?: string
}): DirtyAttributes<any> | undefined => {
  if (originalValues === null) {
    return {
      originalValue: 'non-existent',
      originalProperty:
        labelToDisplay !== undefined
          ? labelToDisplay
          : capitalizeFirst(originalValueKey),
    }
  }

  if (!originalValues) {
    return undefined
  }

  return Object.prototype.hasOwnProperty.call(originalValues, originalValueKey)
    ? {
        originalValue: valueToDisplay !== undefined ? valueToDisplay : '',
        originalProperty:
          labelToDisplay !== undefined
            ? labelToDisplay
            : capitalizeFirst(originalValueKey),
      }
    : undefined
}

const IconWrapper = styled(Flex)`
  box-sizing: border-box;
  height: 24px;
  display: flex;
  align-items: center;
`

const TooltipContent = styled.div`
  max-width: 300px;
  font-size: ${({ theme }) => theme.fontSizes.C};
  text-align: center;
`

const ExclamationMarkIcon = styled(ExclamationMark)`
  cursor: initial;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  margin-top: 4px;
`

interface DirtyIconTooltipProps extends DirtyAttributes {
  preventLabelTooltip: () => void
  isTextStyle?: boolean
}

export const DirtyIconTooltip: React.FC<DirtyIconTooltipProps> = ({
  originalProperty,
  originalValue,
  preventLabelTooltip,
  isTextStyle = false,
}) => {
  return (
    <IconWrapper marginLeft={1} onMouseEnter={preventLabelTooltip}>
      <Tooltip
        content={
          <TooltipContent>
            <span>
              * Out of sync with the original {isTextStyle ? 'Text' : 'Layer'}{' '}
              Style
            </span>
            <br />
            <span>
              Original {originalProperty}{' '}
              {!originalValue.startsWith('has') ? 'is ' : null}
              {originalValue}
            </span>
          </TooltipContent>
        }
        placement="top"
      >
        <ExclamationMarkIcon
          role="img"
          aria-label="Dirty style"
          width={17}
          height={17}
        />
      </Tooltip>
    </IconWrapper>
  )
}
